import { addMonths, format, parse } from 'date-fns';

// Motor único de rebase de série. A aritmética de data que recorrência e parcelas re-implementavam
// idêntica (offset = posição − âncora → addMonths) vive aqui uma vez só. Os dois domínios entram por
// adapters finos tipados (buildRecurringSeriesRebasePlan / buildInstallmentRebasePlan), que só nomeiam
// a posição (occurrenceIndex 0-based vs. installmentNumber) e constroem/ordenam as posições. Ver ADR-0010.

/**
 * Escopo canônico de edição/exclusão de uma série. Vocabulário único do app:
 * - `single` — só a ocorrência/parcela âncora.
 * - `future` — a âncora e as seguintes ("desta em diante").
 * - `all` — a série inteira.
 * Recorrência não aceita `single` no rebase (é tratado no nível da transação) — ver RecurringSeriesRebaseMode.
 */
export type EditScope = 'single' | 'future' | 'all';

/** Entrada do plano: posição neutra (o adapter renomeia pro termo do domínio) + nova data/competência. */
export interface SeriesRebaseEntry {
    position: number;
    date: string;
    invoiceMonth?: string;
}

interface BuildSeriesRebasePlanParams {
    positions: number[];
    anchor: number;
    mode: EditScope;
    baseDate: string;
    baseInvoiceMonth?: string;
}

/**
 * Reposiciona as posições selecionadas pelo `mode` em torno da âncora: cada uma ganha
 * `addMonths(baseDate, posição − âncora)` (e o mês de fatura desliza junto, se houver). As posições
 * são ordenadas defensivamente (no-op para o range já-ascendente da recorrência).
 */
export function buildSeriesRebasePlan({
    positions,
    anchor,
    mode,
    baseDate,
    baseInvoiceMonth,
}: BuildSeriesRebasePlanParams): SeriesRebaseEntry[] {
    const sortedPositions = [...positions].sort((left, right) => left - right);
    const selectedPositions = sortedPositions.filter((position) => {
        if (mode === 'single') return position === anchor;
        if (mode === 'future') return position >= anchor;
        return true;
    });

    const parsedBaseDate = parse(baseDate, 'yyyy-MM-dd', new Date());
    const parsedBaseInvoiceMonth = baseInvoiceMonth
        ? parse(`${baseInvoiceMonth}-01`, 'yyyy-MM-dd', new Date())
        : null;

    return selectedPositions.map((position) => {
        const monthOffset = position - anchor;

        return {
            position,
            date: format(addMonths(parsedBaseDate, monthOffset), 'yyyy-MM-dd'),
            invoiceMonth: parsedBaseInvoiceMonth
                ? format(addMonths(parsedBaseInvoiceMonth, monthOffset), 'yyyy-MM')
                : undefined,
        };
    });
}
